import { useState, ReactNode, useEffect } from 'react';

import { withRedemptionToken } from '@utils/redemptionRoute';
import { FirebaseError } from 'firebase/app';
import { User } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { AuthContext } from './AuthContext';
import * as AuthService from '../services/authService';

type Props = { children: ReactNode };

export const AuthProvider = ({ children }: Props) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const unsubscribe = AuthService.onAuthStateChanged((nextUser) => {
      setUser(nextUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const authenticate = async (action: () => Promise<User>, showError: (error: unknown) => void) => {
    try {
      await action();
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  const login = (email: string, password: string) =>
    authenticate(
      () => AuthService.login(email, password),
      () => toast.error(t('toast.login_error'))
    );

  const signup = (email: string, password: string) =>
    authenticate(
      () => AuthService.signup(email, password),
      (error) => {
        if (error instanceof FirebaseError && error.code === 'auth/email-already-in-use') {
          const token = searchParams.get('token');
          const loginUrl = withRedemptionToken('/login', token);
          toast.error(t('toast.signup_email_in_use'), {
            action: {
              label: t('auth_page.log_in_link'),
              onClick: () => {
                void navigate(loginUrl);
              },
            },
          });
        } else {
          toast.error(t('toast.signup_error'));
        }
      }
    );

  const signInWithGoogle = () =>
    authenticate(AuthService.signInWithGoogle, () => toast.error(t('toast.google_signin_error')));
  const signInWithFacebook = () =>
    authenticate(AuthService.signInWithFacebook, () =>
      toast.error(t('toast.facebook_signin_error'))
    );
  const signInWithApple = () =>
    authenticate(AuthService.signInWithApple, () => toast.error(t('toast.apple_signin_error')));

  const logout = async () => {
    await AuthService.logout();
  };

  const resetPassword = async (email: string) => {
    try {
      await AuthService.resetPassword(email);
      toast.success(t('toast.reset_password_success'));
    } catch (error) {
      toast.error(t('toast.reset_password_error'));
      throw error;
    }
  };

  return (
    <AuthContext
      value={{
        user,
        login,
        signup,
        signInWithGoogle,
        signInWithFacebook,
        signInWithApple,
        logout,
        resetPassword,
        loading,
      }}
    >
      {children}
    </AuthContext>
  );
};
